"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import Select from "react-select";
import { XMarkIcon } from "@heroicons/react/20/solid";

const multiSelectFilterStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: "#4b5563",
    minHeight: "50px",
    fontSize: "14px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "320px",
    maxWidth: "320px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "380px",
    padding: "8px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

export default function BudgetItemsModal({
  open,
  onClose,
  budgetItems = [],
  loading = false,
  netLoading,
  netQuantities,
  selectedItems = [],
  setSelectedItems,
  onAddItems,
  existingChangeItems = [], // ADD THIS
  formatCurrency,
  getFilterOptions,
}) {
  const [filters, setFilters] = useState({
    category: [],
    vendor: [],
    bidArea: [],
    condition: [],
    uom: [],
    search: "",
    showFilter: null,
  });

  const [searchInput, setSearchInput] = useState("");

  if (typeof setSelectedItems !== "function") {
    console.error("BudgetItemsModal: setSelectedItems must be a function");
  }

  const conditionRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!filters.showFilter) return;

      if (
        conditionRef.current &&
        !conditionRef.current.contains(event.target)
      ) {
        const isInsidePortal =
          event.target.closest('[class*="-menu"]') ||
          event.target.closest('[id^="react-select"]');

        if (!isInsidePortal) {
          setFilters((p) => ({ ...p, showFilter: null }));
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filters.showFilter]);

  // Add near top of file, after imports:
  // Replace the helper functions and filteredItems useMemo in BudgetItemsModal

  function normSearch(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/[''"""]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildBudgetBlob(item) {
    const parts = [
      item.itemDescription || item.costCodeDescription || "",
      item.itemCode || item.materialCode || item.laborCode || "",
      item.conditionName || "",
      item.sectionNumber || "",
      item.bidArea || "",
      item.laborId || "",
      item.size || "",
      item.uom || item.container || "",
    ];
    return normSearch(parts.join("  "));
  }

  function tokenVariants(token) {
    const variants = [token];

    const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
    if (fractionCondensed) {
      const [, a, b, c] = fractionCondensed;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
    }

    const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
    if (fractionExpanded) {
      const [, a, b, c] = fractionExpanded;
      variants.push(`${a}${b}${c}`);
    }

    const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
    if (gaugeMatch) {
      const [, num] = gaugeMatch;
      variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
    }

    return variants;
  }

  function scoreBudgetItem(item, tokens) {
    let score = 0;
    const fullQuery = tokens.join(" ");

    const code = normSearch(
      item.itemCode ||
        item.materialCode ||
        item.laborCode ||
        item.laborId ||
        "",
    );
    const desc = normSearch(
      item.itemDescription || item.costCodeDescription || "",
    );

    // Exact / prefix / contains on code
    if (code && code === fullQuery) score += 10000;
    else if (code && code.startsWith(fullQuery)) score += 5000;
    else if (code && code.includes(fullQuery)) score += 2000;

    // Exact / prefix on description
    if (desc === fullQuery) score += 8000;
    else if (desc.startsWith(fullQuery)) score += 3000;

    const fields = [
      { text: code, weight: 50 },
      { text: desc, weight: 30 },
      { text: normSearch(item.conditionName || ""), weight: 15 },
      { text: normSearch(item.bidArea || ""), weight: 10 },
      { text: normSearch(item.sectionNumber || ""), weight: 10 },
      { text: normSearch(item.size || ""), weight: 8 },
      { text: normSearch(item.uom || item.container || ""), weight: 5 },
    ];

    for (const token of tokens) {
      const variants = tokenVariants(token);
      for (const { text, weight } of fields) {
        if (!text) continue;
        for (const v of variants) {
          if (text === v) {
            score += weight * 3;
            break;
          } else if (text.startsWith(v)) {
            score += weight * 2;
            break;
          } else if (text.includes(v)) {
            score += weight;
            break;
          }
        }
      }
    }

    // Shorter descriptions win ties (more specific)
    score -= desc.length * 0.01;

    return score;
  }

  // ─── Replace the filteredItems useMemo ───────────────────────────────────────

  const filteredItems = useMemo(() => {
    if (!Array.isArray(budgetItems)) return [];

    const addedKeys = new Set(
      existingChangeItems
        .map((i) => i.sourceLineItemId?.toString())
        .filter(Boolean),
    );

    // 1. Hard filters
    let results = budgetItems.filter((item) => {
      const itemKey = (
        item.originalLineItemId ||
        item.lineItemId ||
        item._id ||
        item.key
      )?.toString();
      if (itemKey && addedKeys.has(itemKey)) return false;

      if (
        filters.category.length > 0 &&
        !filters.category.includes(item.category)
      )
        return false;

      if (filters.vendor.length > 0) {
        const vendorId = item.vendor?._id || item.vendor;
        if (!filters.vendor.includes(vendorId)) return false;
      }

      if (filters.condition.length > 0) {
        const raw = (item.conditionName || "").trim();
        const match = raw.match(/^\d+\s*[-–—]\s*(.+)$/);
        const normalized = match ? match[1].trim() : raw;
        if (!filters.condition.includes(normalized)) return false;
      }

      if (
        filters.bidArea.length > 0 &&
        !filters.bidArea.includes(item.bidArea || "")
      )
        return false;

      if (filters.uom.length > 0) {
        const itemUom = (item.uom || item.container || "").trim();
        if (!filters.uom.includes(itemUom)) return false;
      }

      return true;
    });

    // 2. Search: filter then score+sort
    const raw = normSearch(filters.search || "");
    if (raw) {
      const tokens = raw.split(/\s+/).filter(Boolean);

      results = results.filter((item) => {
        const blob = buildBudgetBlob(item);
        return tokens.every((token) =>
          tokenVariants(token).some((v) => blob.includes(v)),
        );
      });

      results = results
        .map((item) => ({ item, score: scoreBudgetItem(item, tokens) }))
        .sort((a, b) => b.score - a.score)
        .map((e) => e.item);
    }

    return results;
  }, [budgetItems, filters, existingChangeItems]);

  const handleToggle = (item) => {
    if (typeof setSelectedItems !== "function") return;

    setSelectedItems((prev) => {
      const exists = prev.some((i) => i._id === item._id);

      if (exists) {
        // Deselect → remove it
        return prev.filter((i) => i._id !== item._id);
      }

      // Select → add with default deltaQty = 1
      return [
        ...prev,
        {
          ...item,
          deltaQty: 1,
        },
      ];
    });
  };

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
  });

  const showAlert = (type, title, message, onConfirm) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm:
        onConfirm || (() => setAlert((prev) => ({ ...prev, open: false }))),
    });
  };

  const toggleFilter = (field) => {
    setFilters((prev) => ({
      ...prev,
      showFilter: prev.showFilter === field ? null : field,
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      category: [],
      vendor: [],
      bidArea: [],
      condition: [],
      uom: [],
      search: "",
      showFilter: null,
    });
    setSearchInput("");
  };

  const getNetInfo = (item) => {
    if (!netQuantities) return null;
    // Try the same key resolution order used when building the map
    const candidates = [
      item._id?.toString(),
      item.originalLineItemId?.toString(),
      item.lineItemId?.toString(),
      item.key?.toString(),
    ].filter(Boolean);

    for (const key of candidates) {
      const found = netQuantities.get(key);
      if (found) return found;
    }
    return null;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Helper: Get display price from net quantities (latest price after variances)
  // ═══════════════════════════════════════════════════════════════════════════
  const getDisplayPrice = (item) => {
    const netInfo = getNetInfo(item);

    if (
      netInfo?.latestPrice !== undefined &&
      netInfo.latestPrice > 0 &&
      !item.isContractLabor
    ) {
      return {
        price: netInfo.latestPrice,
        hasPriceChange: netInfo.hasPriceChange || false,
        originalPrice: netInfo.originalPrice ?? item.cost ?? item.price ?? 0,
      };
    }

    const originalPrice = item.cost || item.unitCost || item.unitRate || 0;
    return { price: originalPrice, hasPriceChange: false, originalPrice };
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg max-w-[1600px] max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-600">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Add Items from Budget
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Showing net quantities and latest prices after all variances
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="bg-gray-800 p-4 border-b border-gray-600">
          <div className="flex flex-wrap items-start gap-4">
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setFilters((p) => ({ ...p, search: e.target.value }));
                }}
                placeholder="Search description, code, section..."
                className="w-64 px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500"
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setFilters((p) => ({ ...p, search: "" }));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-start gap-3">
              <div className="relative">
                <button
                  onClick={() => toggleFilter("condition")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    filters.condition.length > 0
                      ? "bg-teal-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Assembly
                  {filters.condition.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {filters.condition.length}
                    </span>
                  )}
                </button>
                {filters.showFilter === "condition" && (
                  <div
                    ref={conditionRef}
                    className="absolute top-full mt-2 left-0 z-50 w-80"
                  >
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        hideSelectedOptions={false}
                        options={getFilterOptions("condition").sort((a, b) => {
                          const numA = parseFloat(
                            a.label.match(/^[\d.]+/)?.[0] || "9999",
                          );
                          const numB = parseFloat(
                            b.label.match(/^[\d.]+/)?.[0] || "9999",
                          );
                          return numA - numB;
                        })}
                        value={getFilterOptions("condition").filter((o) =>
                          filters.condition.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            condition: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleFilter("bidArea")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    filters.bidArea.length > 0
                      ? "bg-green-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Bid Area
                  {filters.bidArea.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {filters.bidArea.length}
                    </span>
                  )}
                </button>

                {filters.showFilter === "bidArea" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={getFilterOptions("bidArea")}
                        value={getFilterOptions("bidArea").filter((o) =>
                          filters.bidArea.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            bidArea: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleFilter("category")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    filters.category.length > 0
                      ? "bg-blue-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Category
                  {filters.category.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {filters.category.length}
                    </span>
                  )}
                </button>

                {filters.showFilter === "category" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={[
                          { value: "M", label: "Material" },
                          { value: "L", label: "Labor" },
                        ]}
                        value={[
                          { value: "M", label: "Material" },
                          { value: "L", label: "Labor" },
                        ].filter((o) => filters.category.includes(o.value))}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            category: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleFilter("vendor")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    filters.vendor.length > 0
                      ? "bg-yellow-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Vendor
                  {filters.vendor.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {filters.vendor.length}
                    </span>
                  )}
                </button>

                {filters.showFilter === "vendor" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={getFilterOptions("vendor")}
                        value={getFilterOptions("vendor").filter((o) =>
                          filters.vendor.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            vendor: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => toggleFilter("uom")}
                  className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                    filters.uom.length > 0
                      ? "bg-purple-600/80 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  UOM
                  {filters.uom.length > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-black/30 rounded-full">
                      {filters.uom.length}
                    </span>
                  )}
                </button>

                {filters.showFilter === "uom" && (
                  <div className="absolute top-full mt-2 left-0 z-50 w-80">
                    <div className="bg-gray-700 rounded-lg shadow-2xl border border-gray-600 p-2">
                      <Select
                        isMulti
                        autoFocus
                        options={getFilterOptions("uom")}
                        value={getFilterOptions("uom").filter((o) =>
                          filters.uom.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            uom: opts ? opts.map((o) => o.value) : [],
                            showFilter: null,
                          }))
                        }
                        menuIsOpen
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiSelectFilterStyles}
                      />
                    </div>
                  </div>
                )}
              </div>

              {((filters.search ||
                filters.category.length ||
                filters.vendor.length ||
                filters.bidArea.length ||
                filters.condition.length ||
                filters.uom.length) && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 rounded hover:bg-red-900/30"
                >
                  Clear all
                </button>
              )) ||
                " "}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              Loading budget items...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No items match your filters
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-700 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Select
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Cat
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Assembly
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Item Code
                  </th>
                  <th className="px-3 py-3 text-left text-xs text-gray-400">
                    Description
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Bid Area
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    Size
                  </th>
                  <th className="px-2 py-3 text-left text-xs text-gray-400">
                    UOM
                  </th>
                  <th className="px-2 py-3 text-right text-xs text-gray-400">
                    Budget Qty
                  </th>
                  {/* ═══════════════════════════════════════════════════════════════
                      UPDATED: Cost column now shows LATEST price with change indicator
                      ═══════════════════════════════════════════════════════════════ */}
                  <th className="px-2 py-3 text-right text-xs text-gray-400">
                    Unit Cost
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                    Net Req.
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                    Ordered
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                    Pending
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                    Available
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {filteredItems.map((item) => {
                  const isSelected = selectedItems.some(
                    (s) => s._id === item._id,
                  );
                  const sel = selectedItems.find((s) => s._id === item._id);

                  // ═══════════════════════════════════════════════════════════════
                  // Get the latest price info from net quantities
                  // ═══════════════════════════════════════════════════════════════
                  const priceInfo = getDisplayPrice(item);
                  console.log(item);

                  return (
                    <tr
                      key={item._id}
                      className={
                        isSelected ? "bg-gray-600/70" : "hover:bg-gray-700"
                      }
                    >
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(item)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-orange-500 rounded focus:ring-orange-500"
                        />
                      </td>
                      <td className="px-2 py-3">
                        <span
                          className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${
                            item.category === "M"
                              ? "bg-blue-900/60 text-blue-300"
                              : "bg-orange-900/60 text-orange-300"
                          }`}
                        >
                          {item.category || "-"}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-300">
                        {item.conditionName || "-"}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-200 font-mono">
                        {item.itemCode ||
                          item.materialCode ||
                          item.laborId ||
                          "-"}
                      </td>
                      <td
                        className="px-3 py-3 text-sm text-gray-200 max-w-md truncate"
                        title={item.itemDescription || item.costCodeDescription}
                      >
                        {item.itemDescription ||
                          item.costCodeDescription ||
                          "-"}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-300">
                        {item.bidArea || "-"}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-300">
                        {item.size || "-"}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-300">
                        {item.uom || item.container || "-"}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-300 text-right">
                        {(item.category === "M"
                          ? item.requiredQty
                          : item.quantity) || 0}
                      </td>

                      {/* ═══════════════════════════════════════════════════════════════
                          UPDATED: Unit Cost cell - shows latest price with change indicator
                          ═══════════════════════════════════════════════════════════════ */}
                      <td className="px-2 py-3 text-right">
                        {netLoading ? (
                          <span className="text-gray-500 animate-pulse">
                            ...
                          </span>
                        ) : (
                          <div className="flex flex-col items-end">
                            <span
                              className={`text-sm font-medium ${
                                priceInfo.hasPriceChange
                                  ? "text-purple-400"
                                  : "text-gray-200"
                              }`}
                            >
                              {formatCurrency(priceInfo.price)}
                            </span>
                            {priceInfo.hasPriceChange &&
                              priceInfo.originalPrice !== priceInfo.price && (
                                <div className="flex items-center gap-1">
                                  <span className="text-[9px] text-gray-500 line-through">
                                    {formatCurrency(priceInfo.originalPrice)}
                                  </span>
                                  <span className="text-[9px] text-purple-400 font-medium">
                                    ↑
                                  </span>
                                </div>
                              )}
                          </div>
                        )}
                      </td>

                      {/* Net Required Column */}
                      <td className="px-3 py-3 text-sm text-right">
                        {netLoading ? (
                          <span className="text-gray-500 animate-pulse">
                            ...
                          </span>
                        ) : (
                          <span className="text-gray-200">
                            {(item.netRequiredQty || 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-right">
                        {netLoading ? (
                          <span className="text-gray-500 animate-pulse">
                            ...
                          </span>
                        ) : (
                          <span
                            className={
                              item.qtyOrdered > 0
                                ? "text-blue-400"
                                : "text-gray-500"
                            }
                          >
                            {(item.qtyOrdered || 0).toFixed(2)}
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-sm text-right">
                        {netLoading ? (
                          <span className="text-gray-500 animate-pulse">
                            ...
                          </span>
                        ) : (
                          <span
                            className={
                              item.qtyPending > 0
                                ? "text-yellow-400"
                                : "text-gray-500"
                            }
                          >
                            {(item.qtyPending || 0).toFixed(2)}
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-sm text-right">
                        {netLoading ? (
                          <span className="text-gray-500 animate-pulse">
                            ...
                          </span>
                        ) : (
                          (() => {
                            const available = item.netRemaining || 0;
                            return (
                              <span
                                className={
                                  available <= 0
                                    ? "text-red-400 font-medium"
                                    : available < 5
                                      ? "text-yellow-400"
                                      : "text-green-400 font-medium"
                                }
                              >
                                {available.toFixed(2)}
                              </span>
                            );
                          })()
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-gray-600 flex justify-between items-center bg-gray-800">
          <span className="text-sm text-gray-400">
            <span className="text-orange-400 font-semibold">
              {selectedItems.length}
            </span>{" "}
            item(s) selected
          </span>
          <div className="space-x-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm bg-gray-600 rounded hover:bg-gray-500 text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onAddItems}
              disabled={selectedItems.length === 0}
              className={`px-6 py-2 text-sm font-medium rounded ${
                selectedItems.length > 0
                  ? "bg-orange-500 hover:bg-orange-600 text-white"
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
            >
              Add Items
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
